import { cn } from '@/lib/cn'

export interface ChipProps {
  label: string
  count?: number
  selected?: boolean
  onClick?: () => void
  onRemove?: () => void
  className?: string
}

/**
 * A toggleable filter pill, or a removable summary of an applied filter.
 *
 * An interactive chip is a real `<button>` rather than a `<span>` wrapping one:
 * the hover and press styling used to live on the span while the focus ring
 * landed on the inner button, so keyboard focus outlined a different shape than
 * the one the mouse highlighted.
 */
export function Chip({
  label,
  count,
  selected,
  onClick,
  onRemove,
  className,
}: ChipProps) {
  const shared = cn(
    'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1',
    'text-sm font-medium whitespace-nowrap',
    'transition-[background-color,border-color,color,translate] duration-100 ease-out',
    selected
      ? 'border-accent bg-accent text-accent-contrast'
      : 'border-line bg-surface-raised text-ink-secondary',
    className,
  )

  const body = (
    <>
      <span className="truncate">{label}</span>
      {count !== undefined ? (
        <span
          className={cn(
            'tabular text-xs',
            selected ? 'text-accent-contrast/75' : 'text-ink-muted',
          )}
        >
          {count.toLocaleString()}
        </span>
      ) : null}
    </>
  )

  const remove = onRemove ? (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove filter ${label}`}
      className={cn(
        '-mr-1 rounded-full p-0.5 transition-colors',
        selected ? 'hover:bg-white/20' : 'hover:bg-surface-active hover:text-ink',
      )}
    >
      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden="true">
        <path
          d="M3 3l6 6M9 3l-6 6"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </button>
  ) : null

  // Removable and clickable at once would nest buttons, which is invalid; the
  // remove affordance wins, since that chip is a summary rather than a control.
  if (onClick && !onRemove) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(
          shared,
          'active:translate-y-px',
          !selected && 'hover:border-line-strong hover:bg-surface-hover hover:text-ink',
        )}
      >
        {body}
      </button>
    )
  }

  return (
    <span className={shared}>
      {body}
      {remove}
    </span>
  )
}
